import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'

// Connects to data-controller="tom-select"
// To alter the sort order of the search results, you can use the sortField setting.  In this is example the original
// order will take precedence over the search score.
// 'tom-select-settings-value': "{\"sortField\":[{\"field\":\"$order\"},{\"field\":\"$score\"}]}"
export default class extends Controller {
  static values = {
    renderer: String,
    settings: Object
  }

  connect () {
    this.settings = this.settingsValue
    this.initTomSelect()
    document.addEventListener('turbo:morph',
      () => this.reinitializeTomSelect())
  }

  disconnect () {
    this.destroyTomSelect()
    document.removeEventListener('turbo:morph',
      () => this.reinitializeTomSelect())
  }

  initTomSelect () {
    if (this.element) {
      if (this.hasRendererValue) {
        const renderer = this.renderers[this.rendererValue]?.call(this) ?? this.customRenderer()
        this.settings = { ...this.settings, ...renderer }
      }
      this.select = new TomSelect(this.element, this.settings)
    }
    if (this.element.classList.contains('rounded-l-none')) {
      this.select.control.style.borderTopLeftRadius = '0'
      this.select.control.style.borderBottomLeftRadius = '0'
    }
    if (this.element.classList.contains('rounded-r-none')) {
      this.select.control.style.borderTopRightRadius = '0'
      this.select.control.style.borderBottomRightRadius = '0'
    }
    if (this.element.classList.contains('py-0')) {
      this.select.control.style.paddingTop = '3px'
      this.select.control.style.paddingBottom = '3px'
    }
  }

  reinitializeTomSelect () {
    const previousValues = this.select.getValue()

    this.destroyTomSelect()
    this.initTomSelect()

    // silently set the value so that the onChange event is not triggered
    this.select.setValue(previousValues, true)
  }

  destroyTomSelect () {
    if (this.select) {
      this.select.destroy()
    }
  }

  get renderers () {
    return { impact: this.impactRenderer, user: this.userRenderer, status: this.statusRenderer }
  }

  // Places the badge StatusBadgeComponent already rendered, carried by each option in
  // data-badge. Nothing about how a badge looks is known here. The wrapper is a flex
  // container because the badge styles itself as a flex child.
  statusRenderer () {
    const badge = (data) => `<div class="flex items-center">${data.badge}</div>`

    return { render: { option: badge, item: badge } }
  }

  // The same avatar the page draws beside a name, so picking a person looks like
  // the badge that results. The markup arrives on the option rather than being
  // rebuilt here: the initials, the colour picked from the name and the Active
  // Storage variant URL all live in Users::AvatarComponent (see
  // UsersHelper#user_options_for_select). It is our own server-rendered markup,
  // which is why it is placed rather than escaped — the name still is.
  userRenderer () {
    const person = (data, escape) => `
      <span class="flex items-center gap-2">
        ${data.avatar ?? ''}
        <span>${escape(data.text)}</span>
      </span>
    `
    return {
      render: {
        option: (data, escape) => `<div>${person(data, escape)}</div>`,
        // tom-select's CSS controls the item root's display, so the flex lives inside
        item: (data, escape) => `<div>${person(data, escape)}</div>`
      }
    }
  }

  impactRenderer () {
    const meter = (data, escape) => {
      const level = Number.parseInt(data.level, 10)
      if (Number.isNaN(level)) return ''

      let dots = ''
      for (let i = 0; i < 4; i++) {
        dots += `<span${i <= level ? ` class="${escape(data.dotClass)}"` : ''}>&#9679;</span>`
      }
      return `<span class="flex items-center gap-0.5 text-[9px] text-gray-300">${dots}</span>`
    }
    return {
      render: {
        option: (data, escape) => {
          const description = data.description
            ? `<p class="mt-0.5 text-xs text-gray-500 group-[.active]:text-gray-200 leading-snug whitespace-normal">${escape(data.description)}</p>`
            : ''
          return `
            <div class="group">
              <div class="flex items-center gap-2">
                ${meter(data, escape)}
                <span class="font-medium">${escape(data.text)}</span>
              </div>
              ${description}
            </div>
          `
        },
        item: (data, escape) => {
          // tom-select's CSS controls the item root's display, so flex lives on an inner wrapper
          return `
            <div>
              <span class="flex items-center gap-2">
                ${meter(data, escape)}
                <span>${escape(data.text)}</span>
              </span>
            </div>
          `
        }
      }
    }
  }

  customRenderer () {
    return {
      render: {
        option: (data, escape) => {
          return `
            <div class="ts-custom-renderer group">
              <span class="ts-custom-renderer checkmark">
                <svg class="hidden group-[.selected]:block" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                </svg>
              </span>
              <p class="ts-custom-renderer text">
                ${escape(data.text)}
                <span class="ts-custom-renderer subtitle line-clamp-1">
                  ${escape(data.subtitle)}
                </span>
              </p>
            </div>
          `
        },
        item: (data, escape) => {
          return `
            <div title="${escape(data.subtitle)}">
              ${escape(data.text)}
            </div>
          `
        }
      },
      searchField: ['text', 'subtitle']
    }
  }
}
